import { Button } from '@repo/design-system/components/ds/button';
import { FlexRow } from '@repo/design-system/components/layout/flex-row';
import { Fieldset } from '@repo/design-system/components/ui/fieldset';
import { Form } from '@repo/design-system/components/ui/form';
import { Select, SelectItem } from '@repo/design-system/components/ui/select';
import { toast } from '@repo/design-system/components/ui/toaster';
import { useCreateMapCategoryMutation, useCreateMapSubcategoryMutation, useGetGameMapsQuery } from '@repo/store/maps/api/mapsApi';
import { useAppSelector } from '@repo/store/maps/hooks';
import { selectCurrentMapInfo, selectExternalMapId, selectTitleId } from '@repo/store/maps/slices/mapData';
import { getGameMap } from '@repo/wemod-api-client/maps-api-client';
import { useCallback, useMemo, useState } from 'react';
const normalizeTitle = (title: string) => title.trim().toLocaleLowerCase();
export const ImportCategoriesAndSubcategoriesForm = () => {
  const externalMapId = useAppSelector(selectExternalMapId);
  const titleId = useAppSelector(selectTitleId);
  const currentMapInfo = useAppSelector(selectCurrentMapInfo);
  const [selectedSourceMapId, setSelectedSourceMapId] = useState('');
  const [isImporting, setIsImporting] = useState(false);
  const [createCategory] = useCreateMapCategoryMutation();
  const [createSubcategory] = useCreateMapSubcategoryMutation();
  const {
    data: maps = [],
    isFetching
  } = useGetGameMapsQuery({
    titleId: titleId ?? '',
    debug: true
  }, {
    skip: !titleId
  });
  const sourceMaps = useMemo(() => maps.filter(map => map.externalId !== externalMapId && map.titleId === titleId), [externalMapId, maps, titleId]);
  const selectedSourceMap = sourceMaps.find(map_0 => map_0.externalId === selectedSourceMapId);
  const handleImport = useCallback(async () => {
    if (!externalMapId || !selectedSourceMapId) return;
    setIsImporting(true);
    try {
      const [sourceMap, destinationMap] = await Promise.all([getGameMap(selectedSourceMapId, 'en-US', {
        debug: true
      }), getGameMap(externalMapId, 'en-US', {
        debug: true
      })]);
      const categoryExternalIdMap = new Map<string, string>();
      let copiedCategoryCount = 0;
      let copiedSubcategoryCount = 0;
      let skippedCategoryCount = 0;
      let skippedSubcategoryCount = 0;
      let omittedSubcategoryCount = 0;
      const destinationCategoriesByTitle = new Map(destinationMap.metadata.categories.filter(category => !category.clientOnly).map(category_0 => [normalizeTitle(category_0.title), category_0]));
      const destinationDisplayOrders = new Set(destinationMap.metadata.categories.map(category_1 => category_1.displayOrder).filter((displayOrder): displayOrder is number => displayOrder != null));
      const destinationSubcategoryKeys = new Set(destinationMap.metadata.subcategories.map(subcategory => [subcategory.categoryExternalId, normalizeTitle(subcategory.title)].join(':')));
      for (const category_2 of sourceMap.metadata.categories.filter(category_3 => !category_3.clientOnly)) {
        const existingCategory = destinationCategoriesByTitle.get(normalizeTitle(category_2.title));
        if (existingCategory) {
          categoryExternalIdMap.set(category_2.externalId, existingCategory.externalId);
          skippedCategoryCount += 1;
          continue;
        }
        const copiedCategory = await createCategory({
          mapExternalId: externalMapId,
          category: {
            title: category_2.title,
            displayOrder: category_2.displayOrder != null && !destinationDisplayOrders.has(category_2.displayOrder) ? category_2.displayOrder : null,
            color: category_2.color,
            visibleByDefault: category_2.visibleByDefault,
            excludeFromChecklists: category_2.excludeFromChecklists,
            description: category_2.description
          }
        }).unwrap();
        categoryExternalIdMap.set(category_2.externalId, copiedCategory.externalId);
        destinationCategoriesByTitle.set(normalizeTitle(copiedCategory.title), copiedCategory);
        if (copiedCategory.displayOrder != null) {
          destinationDisplayOrders.add(copiedCategory.displayOrder);
        }
        copiedCategoryCount += 1;
      }
      const subcategoriesToCreate = [];
      for (const subcategory_0 of sourceMap.metadata.subcategories) {
        const categoryExternalId = categoryExternalIdMap.get(subcategory_0.categoryExternalId);
        if (!categoryExternalId) {
          omittedSubcategoryCount += 1;
          continue;
        }
        const subcategoryKey = [categoryExternalId, normalizeTitle(subcategory_0.title)].join(':');
        if (destinationSubcategoryKeys.has(subcategoryKey)) {
          skippedSubcategoryCount += 1;
          continue;
        }
        destinationSubcategoryKeys.add(subcategoryKey);
        subcategoriesToCreate.push({
          mapExternalId: externalMapId,
          subcategory: {
            title: subcategory_0.title,
            icon: subcategory_0.icon,
            categoryExternalId,
            excludeFromChecklists: subcategory_0.excludeFromChecklists,
            description: subcategory_0.description
          }
        });
      }
      await Promise.all(subcategoriesToCreate.map(subcategory_1 => createSubcategory(subcategory_1).unwrap()));
      copiedSubcategoryCount = subcategoriesToCreate.length;
      toast({
        title: 'Categories and subcategories imported',
        description: `Imported ${copiedCategoryCount} categories and ${copiedSubcategoryCount} subcategories. Skipped ${skippedCategoryCount} categories and ${skippedSubcategoryCount} subcategories that already existed. Omitted ${omittedSubcategoryCount} subcategories without importable parent categories.`,
        variant: 'success'
      });
      setSelectedSourceMapId('');
    } catch (error) {
      toast({
        title: 'Failed to import categories and subcategories',
        description: JSON.stringify(error),
        variant: 'error'
      });
    } finally {
      setIsImporting(false);
    }
  }, [createCategory, createSubcategory, externalMapId, selectedSourceMapId]);
  return <Form spacing="md" onSubmit={e => {
    e.preventDefault();
    void handleImport();
  }}>
            <Fieldset legend="Import categories and subcategories" legendHidden spacing="md">
                <Select label="Import from map" value={selectedSourceMapId} onValueChange={setSelectedSourceMapId} placeholder="Select map" size="sm" fullWidth aria-label="Select map to import categories and subcategories from" trigger={<Button color="secondary" size="sm" fullWidth>
                            {selectedSourceMap?.name ?? 'Select map'}
                        </Button>}>
                    {sourceMaps.map(map_1 => <SelectItem key={map_1.externalId} item={{
          label: map_1.name,
          value: map_1.externalId
        }}>
                            {map_1.name}
                        </SelectItem>)}
                </Select>
            </Fieldset>

            <FlexRow gap="4" justify="end">
                <Button type="submit" color="success" size="sm" disabled={!selectedSourceMapId || !externalMapId || isImporting || isFetching}>
                    {isImporting ? 'Importing...' : `Import to ${currentMapInfo?.name ?? 'current map'}`}
                </Button>
            </FlexRow>
        </Form>;
};